'use client';

/**
 * `useHolderPlinths` — batch-fetched plinth materials for every holder
 * currently on a board, keyed by lowercased address.
 *
 * ## Why this hook exists
 *
 * Player plinth selections are stored per-user in the `users` DB table
 * (column `player_plinth`). Until this hook landed, the lobby's
 * `seatPedestalMaterial` resolver only knew about the *connected
 * viewer's* plinth (read from `usePlayerPlinthMaterial` /
 * `useEffectivePlayerPlinth`); every OTHER holder defaulted to the
 * neutral `'stone'` slab, making the boardroom look uniform regardless
 * of how many of those tiles' actual owners had picked custom
 * materials in the profile modal.
 *
 * The fix is to surface every holder's plinth alongside the identity
 * data the lobby already pulls. The `/api/users` POST endpoint
 * (originally a batch displayName / avatarUrl / level fetcher) now
 * also returns each address's `plinthId` (validated against the
 * registry — retired ids surface as `null`). This hook stitches that
 * onto a `Map` the canvas overlays can read in O(1) per tile per
 * frame.
 *
 * ## Cache & request shape
 *
 * - One POST per board change, capped at 100 addresses (the API's
 *   per-request limit). Larger boards split into multiple requests
 *   batched in parallel; results are merged into a single map.
 * - The map is referentially stable across renders that don't
 *   change the holder set (`useMemo` keyed on the sorted holder
 *   list joined by `|`). Adding/removing a tile triggers a refetch;
 *   tax-only updates (price / runway) don't.
 * - Live mid-board buyouts / claims that swap a holder are picked up
 *   on the next render that includes the new address — the canvas
 *   already re-renders on those events, so the plinth swap follows
 *   within ~1 frame of the avatar/identity swap.
 * - Reserved seats include their reserved claimant in the lookup
 *   even when the seat isn't held yet, so the previewed plinth on
 *   a reservation matches the plinth the claimant will see on their
 *   own seat after they claim.
 *
 * ## Failure mode
 *
 * Transient failures (network error, 429 rate limit, 5xx upstream)
 * trigger an exponential backoff retry — up to 3 attempts at
 * 600ms / 1.4s / 3.3s, with ±25% jitter. `Retry-After` headers are
 * honoured when present. While retries are in flight the hook keeps
 * the previously-resolved `Map` so the canvas doesn't flicker back
 * to `'stone'` mid-recovery; on terminal failure it falls through
 * to the pre-fix behaviour (default slab for every holder).
 *
 * Critically, `lastFetchedKey` is only pinned after a *fully
 * successful* merge — earlier revisions pinned it on fetch
 * initiation, which meant a single failed first attempt would
 * permanently mark the holder set as "fetched" and the hook would
 * never retry for the rest of the session, leaving entire tabs
 * stuck with default plinths until reload. That was the
 * user-reported "random stone plinths across tabs" symptom: each
 * tab raced the shared 30-req/min `/api/users` rate limit
 * differently, and losers stayed wrong.
 */

import { useEffect, useMemo, useRef, useState } from 'react';

import type { SeatData } from '@/components/grid';
import type { PlinthMaterialId } from '../theming/plinths/types';
import {
  coercePlinthMaterialId,
  THEMING_NONE_SENTINEL,
} from '@/lib/theming-validation';

/** API contract from `/api/users` POST — mirrors the route handler's
 *  `result` shape exactly, with only the fields this hook reads. */
interface BatchUserResult {
  plinthId: string | null;
}

interface BatchUserResponse {
  data?: Record<string, BatchUserResult | undefined>;
}

/** Empty stable reference so the canvas doesn't see a fresh `Map`
 *  every render before the first response lands. */
const EMPTY_MAP: ReadonlyMap<string, PlinthMaterialId> = new Map();

/**
 * Build the canonical holder list for a seat array — deduplicated,
 * lowercased, alpha-sorted. Sorting + joining gives a stable cache
 * key that doesn't change when the holder *set* is identical but the
 * seat order shuffles (e.g. price-floor re-sort).
 *
 * Reserved-but-unclaimed seats include the `reservedFor` address so
 * the claimant's plinth previews correctly on the seat before claim
 * resolves — same pattern `seatPedestalMaterial` uses to fall back
 * to the reserved address for its identity hash. Vacant reclaim tiles
 * include `forfeitPlinthHolder` so the forfeited user's slab persists
 * until the Dutch / anti-snipe window closes.
 */
function collectHolderAddresses(seats: SeatData[]): {
  list: string[];
  cacheKey: string;
} {
  const seen = new Set<string>();
  for (const seat of seats) {
    const holder = seat.holder?.toLowerCase();
    if (holder) seen.add(holder);
    const reserved = seat.reservedFor?.toLowerCase();
    if (reserved) seen.add(reserved);
    const ghost = seat.forfeitPlinthHolder?.toLowerCase();
    if (ghost) seen.add(ghost);
  }
  const list = Array.from(seen).sort();
  return { list, cacheKey: list.join('|') };
}

/**
 * Slice an address list into chunks the batch API can handle. The
 * `/api/users` POST route caps each request at 100 addresses; we
 * mirror that constant here rather than importing from the route so
 * tests don't need a server runtime.
 */
function chunkAddresses(addresses: string[], size = 100): string[][] {
  if (addresses.length <= size) return addresses.length === 0 ? [] : [addresses];
  const out: string[][] = [];
  for (let i = 0; i < addresses.length; i += size) {
    out.push(addresses.slice(i, i + size));
  }
  return out;
}

/** Maximum number of retries per `cacheKey` after a transient failure
 *  (network error, 429, 5xx). 3 attempts at 600ms / 1.4s / 3.3s gives
 *  the rate limiter a fresh window to open without burning the user's
 *  budget if `/api/users` is genuinely down. */
const MAX_RETRIES = 3;

/** Initial backoff delay in ms; doubled (with mild jitter) on each
 *  attempt so coordinated tab refreshes stagger naturally instead of
 *  thundering-herd-ing the rate limiter. */
const RETRY_BASE_MS = 600;

/** Compute the retry delay for `attempt` (0-indexed). Jitter ±25% so
 *  multiple lobby tabs don't lock-step into the same /api/users window. */
function retryDelayMs(attempt: number, retryAfterMs?: number): number {
  if (retryAfterMs !== undefined && retryAfterMs > 0) return retryAfterMs;
  const base = RETRY_BASE_MS * Math.pow(2.3, attempt);
  const jitter = base * 0.25 * (Math.random() * 2 - 1);
  return Math.max(200, Math.round(base + jitter));
}

/** Distinguishes a "we should try again" failure (network, 429, 5xx)
 *  from a "this request is malformed and will keep failing" failure
 *  (4xx other than 429). The retryable bucket gets the backoff loop;
 *  non-retryable failures stop after the first attempt to avoid
 *  hammering the API on a permanent error. */
function isRetryableStatus(status: number): boolean {
  if (status === 429) return true;
  if (status >= 500 && status < 600) return true;
  return false;
}

/**
 * Fetch and merge plinth ids for every holder currently on the board.
 * Returns a stable `Map` reference between identical holder sets.
 */
export function useHolderPlinths(seats: SeatData[]): ReadonlyMap<string, PlinthMaterialId> {
  // Holder list rebuilt only when the *set* changes. Keyed off
  // `cacheKey` so prop-identity changes from `seats` re-rendering
  // don't force a refetch.
  const { list, cacheKey } = useMemo(() => collectHolderAddresses(seats), [seats]);

  const [map, setMap] = useState<ReadonlyMap<string, PlinthMaterialId>>(EMPTY_MAP);

  // The seat list typically re-renders many times per second on a
  // live board (price ticks, animation states). Wrapping the fetch
  // in a `cacheKey` ref guard means we only re-issue the network
  // call when the *holder set* actually changes — but we MUST set
  // the ref only after a successful fetch, otherwise a transient
  // failure on first attempt would pin `lastFetchedKey` to the
  // current cacheKey forever and the hook would never retry, leaving
  // the canvas painting every holder as the default `'stone'` slab
  // for the rest of the session. That was the user-reported "random
  // stone plinths across tabs" symptom — different tabs lost the
  // shared 30-req/min `/api/users` rate-limit roulette in different
  // ways and the loser stayed wrong.
  const lastFetchedKey = useRef<string | null>(null);

  useEffect(() => {
    if (cacheKey === lastFetchedKey.current) return;

    if (list.length === 0) {
      // Empty board (or all vacant) — drop any stale map. We compare
      // against `EMPTY_MAP` first so we don't trigger an unnecessary
      // re-render in the empty-stay case. Mark the empty key as
      // "fetched" so we don't re-enter the empty branch every render.
      lastFetchedKey.current = cacheKey;
      setMap((prev) => (prev === EMPTY_MAP ? prev : EMPTY_MAP));
      return;
    }

    let cancelled = false;
    let retryHandle: ReturnType<typeof setTimeout> | null = null;

    const attempt = async (attemptIdx: number): Promise<void> => {
      try {
        const chunks = chunkAddresses(list);
        let retryable = false;
        let retryAfterMs: number | undefined;
        const responses = await Promise.all(
          chunks.map(async (addresses): Promise<BatchUserResponse | null> => {
            let res: Response;
            try {
              res = await fetch('/api/users', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ addresses }),
              });
            } catch (err) {
              // Network failure — always retryable (offline, DNS, etc.).
              retryable = true;
              if (process.env.NODE_ENV !== 'production') {
                console.warn('[useHolderPlinths] /api/users network error', err);
              }
              return null;
            }
            if (!res.ok) {
              if (isRetryableStatus(res.status)) {
                retryable = true;
                // Honour `Retry-After` (seconds) when the route opts
                // to throttle so we don't pile on inside the same
                // window. Falls back to the exponential backoff below.
                const ra = res.headers.get('retry-after');
                if (ra) {
                  const secs = Number(ra);
                  if (Number.isFinite(secs) && secs > 0) retryAfterMs = secs * 1000;
                }
              }
              if (process.env.NODE_ENV !== 'production') {
                console.warn(
                  `[useHolderPlinths] /api/users returned ${res.status}` +
                    (retryable ? ` (will retry in ~${retryDelayMs(attemptIdx, retryAfterMs)}ms)` : ' (giving up)'),
                );
              }
              return null;
            }
            try {
              return (await res.json()) as BatchUserResponse;
            } catch (err) {
              // Body parse failure — treat as retryable since a fresh
              // request might land cleanly. Keeps the same backoff so
              // we don't spin tightly on a flaky upstream.
              retryable = true;
              if (process.env.NODE_ENV !== 'production') {
                console.warn('[useHolderPlinths] /api/users body parse error', err);
              }
              return null;
            }
          }),
        );
        if (cancelled) return;

        const allChunksOk = responses.every((r) => r !== null);
        if (!allChunksOk) {
          if (retryable && attemptIdx < MAX_RETRIES) {
            retryHandle = setTimeout(() => {
              if (!cancelled) void attempt(attemptIdx + 1);
            }, retryDelayMs(attemptIdx, retryAfterMs));
          }
          // Give up: leave the existing map in place (don't clobber a
          // good prior result with EMPTY_MAP) and don't pin
          // `lastFetchedKey`, so a later effect run for the same
          // holder set can re-enter and try again.
          return;
        }

        const next = new Map<string, PlinthMaterialId>();
        for (const resp of responses) {
          // `resp.data` shape is `Record<lowerAddress, entry | undefined>`;
          // anything that doesn't match (e.g. a 200 response that wraps
          // an `{ error }` envelope from a future API change) just
          // produces an empty bucket and the canvas keeps the default
          // slab for those holders.
          if (!resp?.data || typeof resp.data !== 'object') continue;
          for (const [address, entry] of Object.entries(resp.data)) {
            if (!entry || typeof entry !== 'object' || !entry.plinthId) continue;
            // Defensive: even though the route already validated, the
            // payload could contain a stale id from a registry change
            // mid-flight. Validate again client-side and silently drop
            // unknowns rather than ship a `null` back into the canvas.
            // The validator can also return the `'none'` sentinel
            // (meaning the user explicitly cleared their plinth in the
            // wizard); treat that the same as "no plinth on file" so
            // `seatPedestalMaterial` falls through to `'stone'`.
            const validated = coercePlinthMaterialId(entry.plinthId);
            if (!validated || validated === THEMING_NONE_SENTINEL) continue;
            next.set(address.toLowerCase(), validated);
          }
        }
        // Pin the cacheKey only on a fully-successful merge — every
        // partial failure above bails out before this point.
        lastFetchedKey.current = cacheKey;
        if (process.env.NODE_ENV !== 'production') {
          // Diagnostic for "everyone's stone when wallet is connected":
          // compare `requested` against `withPlinth`. If they're equal
          // and high, the API is returning records but most users
          // really haven't picked a material; if `withPlinth` is 0,
          // the JSON response shape is empty (and `seatPedestalMaterial`
          // falls back to default `'stone'` for every holder).
          // eslint-disable-next-line no-console -- gated dev-only diagnostic
          console.info('[useHolderPlinths] merged', {
            requested: list.length,
            withPlinth: next.size,
          });
        }
        setMap(next);
      } catch (err) {
        if (cancelled) return;
        // Unexpected synchronous throw inside the loop body. Treat
        // identically to a retryable failure so we get one more shot.
        if (process.env.NODE_ENV !== 'production') {
          console.warn('[useHolderPlinths] unexpected error', err);
        }
        if (attemptIdx < MAX_RETRIES) {
          retryHandle = setTimeout(() => {
            if (!cancelled) void attempt(attemptIdx + 1);
          }, retryDelayMs(attemptIdx));
        }
      }
    };

    void attempt(0);

    return () => {
      cancelled = true;
      if (retryHandle !== null) clearTimeout(retryHandle);
    };
    // Intentionally only depend on `cacheKey` (the canonicalised
    // string). The `list` array is rebuilt by `useMemo` whenever
    // `seats` changes by reference, even when its *contents* (and
    // therefore `cacheKey`) are identical — including it in deps
    // would re-fire the effect on every parent render that hands us
    // a new `seats` reference, abort the in-flight retry sequence,
    // and start a fresh attempt 0 each time. The captured `list`
    // closure value matches the captured `cacheKey` because both
    // come from the same memo, so it's safe to read without listing
    // it as a dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  return map;
}
